import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [portfolioDropdownOpen, setPortfolioDropdownOpen] = useState(false);
  const [insightsDropdownOpen, setInsightsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Portfolio', 
      path: '/portfolio',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Property Showcase', path: '/portfolio' },
        { name: 'Available Listings', path: '/units' }
      ]
    },
    { name: 'Services', path: '/services' },
    { 
      name: 'Insights', 
      path: '/news',
      hasDropdown: true,
      dropdownItems: [
        { name: 'News', path: '/news' },
        { name: 'Blogs', path: '/blogs' }
      ]
    },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo">
            <div className="flex items-center gap-3">
              <img
                src="https://res.cloudinary.com/dzk6j0kr2/image/upload/v1784037648/gn-management/brand/gn_logo.png"
                alt="GN Management"
                className="h-14"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div
                  key={link.path}
                  className="relative group"
                  onMouseEnter={() => {
                    if (link.name === 'Portfolio') setPortfolioDropdownOpen(true);
                    if (link.name === 'Insights') setInsightsDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (link.name === 'Portfolio') setPortfolioDropdownOpen(false);
                    if (link.name === 'Insights') setInsightsDropdownOpen(false);
                  }}
                >
                  <button
                    className={`font-medium transition-colors duration-200 flex items-center gap-1 py-2 ${
                      (link.name === 'Portfolio' && (location.pathname === '/portfolio' || location.pathname === '/units')) ||
                      (link.name === 'Insights' && (location.pathname === '/news' || location.pathname === '/blogs' || location.pathname.startsWith('/news/') || location.pathname.startsWith('/blogs/')))
                        ? 'text-[#20B2AA]'
                        : 'text-gray-700 hover:text-[#20B2AA]'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                      (link.name === 'Portfolio' && portfolioDropdownOpen) || (link.name === 'Insights' && insightsDropdownOpen) ? 'rotate-180' : ''
                    }`} />
                  </button>
                  
                  {/* Dropdown Menu - positioned to overlap slightly with button */}
                  {((link.name === 'Portfolio' && portfolioDropdownOpen) || (link.name === 'Insights' && insightsDropdownOpen)) && (
                    <div className="absolute top-full left-0 pt-1 w-56 z-50">
                      <div className="bg-white rounded-lg shadow-xl py-2 border border-gray-100 animate-fade-in">
                        {link.dropdownItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                              location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                                ? 'bg-[#20B2AA]/10 text-[#20B2AA]'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-[#20B2AA]'
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#20B2AA]'
                      : 'text-gray-700 hover:text-[#20B2AA]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button
                data-testid="nav-cta-btn"
                className="bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-full px-6 hover:-translate-y-0.5 transition-all duration-200"
              >
                Get in Touch
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path} className="space-y-2">
                  <div className="py-3 px-4 font-medium text-gray-900">
                    {link.name}
                  </div>
                  {link.dropdownItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block py-2 px-4 ml-4 rounded-lg font-medium transition-colors duration-200 ${
                        location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                          ? 'bg-[#20B2AA]/10 text-[#20B2AA]'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`block py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'bg-[#20B2AA]/10 text-[#20B2AA]'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link to="/contact">
              <Button
                data-testid="mobile-nav-cta-btn"
                className="w-full bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-full py-3 mt-4"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
